import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import MemberMobileBookingHeader from '../components/MemberMobileBookingHeader'

const CURRENCY_SYMBOLS = {
  GBP: '£',
  USD: '$',
  EUR: '€',
}

function formatPrice(value, currency, locale) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  const code = currency || ''
  const symbol = CURRENCY_SYMBOLS[code] || ''
  const formatted = numeric.toLocaleString(locale, { minimumFractionDigits: numeric % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })
  if (symbol) return `${symbol}${formatted}`
  if (code) return `${formatted} ${code}`
  return formatted
}

function formatDuration(durationMinutes, prepTime, locale) {
  const parts = []
  if (Number.isFinite(durationMinutes)) {
    parts.push(new Intl.NumberFormat(locale, { style: 'unit', unit: 'minute', unitDisplay: 'short' }).format(durationMinutes))
  }
  return parts.join(' • ')
}

export default function MemberSpaServicesPage({ clubCode }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const navigate = useNavigate()
  const { memberPortalApiGet } = useMemberPortalApi()
  const { t } = useOptionalPrivanaI18n()
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    let cancelled = false

    async function loadServices() {
      if (!clubCode) return
      setLoading(true)
      setError(null)

      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/spa/services`)
        const json = await res.json().catch(() => null)

        if (!res.ok) {
          throw new Error(json?.error || 'unable_to_load_services')
        }

        const serviceData = Array.isArray(json?.services) ? json.services : []
        if (!cancelled) {
          setServices(serviceData)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load spa services', err)
          setError('facilityBooking.serviceFailed')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadServices()

    return () => {
      cancelled = true
    }
  }, [clubCode, memberPortalApiGet])

  const filteredServices = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    const activeServices = services.filter((service) => service?.id)
    if (!normalizedQuery) return activeServices

    return activeServices.filter((service) => {
      const name = String(service?.name || '').toLowerCase()
      const description = String(service?.description || '').toLowerCase()
      return name.includes(normalizedQuery) || description.includes(normalizedQuery)
    })
  }, [query, services])

  return (
    <div className="mp-panel">
      <MemberMobileBookingHeader
        className="mp-panel__header mp-spa-services-header"
        eyebrow={memberText("portalPresentation.member_spa")}
        title={memberText("portalPresentation.member_choose_a_service")}
        desktopTitle={memberText("portalPresentation.member_choose_your_service")}
        backLabel={memberText("portalPresentation.member_back_to_facilities")}
        backTo={`/${clubCode}/book-facilities`}
        secondaryBeforeBack
        secondaryAction={
          <div className="mp-spa-search">
            <input
              type="text"
              placeholder={memberText("portalPresentation.member_search_services")}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        }
      />

      {error ? <div className="mp-warning">{memberText(error)}</div> : null}

      {loading ? (
        <p className="mp-text-muted">{memberText("portalPresentation.member_loading_services")}</p>
      ) : filteredServices.length === 0 ? (
        <p className="mp-text-muted">{memberText("portalPresentation.member_no_spa_services_available")}</p>
      ) : (
        <div className="mp-spa-service-list">
          {filteredServices.map((service, index) => {
            const duration = formatDuration(service?.duration_minutes, service?.prep_time, locale)
            const price = formatPrice(service?.member_price ?? service?.price, service?.currency, locale)
            const category = service?.category?.trim() || null
            const previousCategory = index > 0 ? filteredServices[index - 1]?.category?.trim() : null
            const showCategory = category && category !== previousCategory

            return (
              <React.Fragment key={service.id}>
                {showCategory ? <div className="mp-spa-category">{category}</div> : null}
                <button
                  type="button"
                  className="mp-golf-course-card mp-spa-service-card"
                  onClick={() =>
                    navigate(`/${clubCode}/spa/${service.id}`, {
                      state: { service },
                    })
                  }
                >
                  <div className="mp-spa-service-card__image-wrapper">
                    {service?.image_url ? <img className="mp-spa-service-card__image" src={service.image_url} alt="" loading="lazy" /> : <span className="mp-spa-service-card__image-fallback" aria-hidden="true">{memberText("portalPresentation.member_spa")}</span>}
                  </div>
                  <div className="mp-spa-service-card__meta">
                    <p className="mp-golf-course-label">{memberText("portalPresentation.member_service")}</p>
                    <div className="mp-spa-service-card__name">{service?.name || memberText("portalPresentation.member_spa_service")}</div>
                    <div className="mp-spa-service-card__details">
                      {duration ? <span>{duration}</span> : null}
                      {price ? <span className="mp-spa-service-card__price">{price}</span> : null}
                    </div>
                    {service?.description ? (
                      <div className="mp-spa-service-card__description">{service.description}</div>
                    ) : null}
                  </div>
                  <div className="mp-golf-course-chip">{memberText("portalPresentation.member_select")}</div>
                </button>
              </React.Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}
